// 1. Third-party plugins and files
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

// 2. Components
import Spinner from "../../components/spinner/Spinner";

// 6. Services
import { logout } from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// 9. Config
import { appConfig } from "../../config/appConfig";

// 13. Styles
import "../indiaPdf/IndiaPdf.scss";

type AdminEntry = {
  [x: string]: any;
  id: number;
  name: string;
  uploadBy: string;

  isActive: boolean;
};

const StateActive = () => {
  // useStates
  const [dataList, setDataList] = useState<AdminEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 39;

  // useNavigate
  const navigate = useNavigate();

  // useEffect
  useEffect(() => {
    fetchData();
  }, []);

  // Methods
  const fetchData = async () => {
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(appConfig.serviceUrls.getActiveState, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);

        return;
      }

      if (!response.ok) throw new Error("Failed to fetch data");
      const data: AdminEntry[] = await response.json();
      setDataList(data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleIsActive = async (index: number) => {
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }
    setLoading(true);

    const selectedEntry = dataList.find((item) => item.id === index);
    if (!selectedEntry) {
      console.error("No entry found for id:", index);
      return;
    }
    const username = getStorageItemLocal("username") ?? "";
    const updatedEntry = {
      ...selectedEntry,
      isActive: !selectedEntry.isActive,
      uploadBy: username,
    };

    const updatedList = dataList.map((item) =>
      item.id === index ? updatedEntry : item
    );
    setDataList(updatedList);
    const url = `${appConfig.serviceUrls.getActiveState}/${index}`;

    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedEntry),
      });

      if (!response.ok) throw new Error("Failed to save data");

      alert("Data saved successfully");
      fetchData(); // Refresh the data if needed
    } catch (error) {
      console.error("Error updating data:", error);
      alert("Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="container-wraper">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          paddingBottom: "5px",
        }}
      >
        <Typography className="bold-header-text">
          <b>State Status</b>{" "}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 3,
          marginBottom: 2,
        }}
      >
        {loading ? (
          <>
            {" "}
            <Spinner></Spinner>
          </>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              marginTop: 2,
              width: "100%",
            }}
          >
            {dataList?.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow className="custom-table-head-row">
                    <TableCell>
                      <b>State</b>
                    </TableCell>

                    <TableCell className="nowrap">
                      <b>Active / Disable</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>

                        <TableCell className="nowrap">
                          <Box
                            onClick={() => handleIsActive(item.id)}
                            className="status-wrapper"
                            style={{ cursor: "pointer" }}
                          >
                            <Box
                              className={`status-toggle1 ${
                                item.isActive ? "active" : "Disable"
                              }`}
                            >
                              <Box className="status-circle" />
                            </Box>

                            <Typography className="active-txt" component="span">
                              {item.isActive ? "Active" : "Disable"}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ padding: "20px" }}>
                <Typography>No Data Found</Typography>
              </Box>
            )}
          </TableContainer>
        )}
      </Box>{" "}
    </Box>
  );
};

export default StateActive;
